/**
 * Module 00 — Simple Mode · static content (ported from `simple_mode.jsx`).
 * Workspaces/clients are hardcoded in the prototype; production drives the
 * workspace list off the user's accessible projects (see module API table).
 */

export interface Workspace {
  name: string;
  slug: string;
  /** Optional metadata — present in the demo set, absent for real projects. */
  region?: string;
  nodes?: string;
  color: string;
}

/** Deterministic accent palette for project-derived workspaces. */
const WS_PALETTE = ['#8b5cf6', '#3b82f6', '#06b6d4', '#22c55e', '#f59e0b', '#ec4899'];

/**
 * Map the user's accessible projects to Simple Mode workspaces. `slug` is the
 * project id — it becomes the workspace id the gateway scopes keys to.
 */
export function workspacesFromProjects(projects: { id: string; name: string }[]): Workspace[] {
  return projects.map((p, i) => ({
    name: p.name,
    slug: p.id,
    color: WS_PALETTE[i % WS_PALETTE.length],
  }));
}

export const WORKSPACES: Workspace[] = [
  { name: 'Customer Operations', slug: 'customer_ops', region: 'us-east-1', nodes: '2,759', color: '#8b5cf6' },
  { name: 'Sales Intelligence', slug: 'sales_intel', region: 'us-east-1', nodes: '8,120', color: '#3b82f6' },
  { name: 'Support Desk', slug: 'support_desk', region: 'eu-west-1', nodes: '1,044', color: '#06b6d4' },
  { name: 'Revenue Ops', slug: 'revenue_ops', region: 'us-west-2', nodes: '4,887', color: '#22c55e' },
];

export interface FlowStep {
  icon: string;
  color: string;
  title: string;
  desc: string;
}

export const FLOW: FlowStep[] = [
  { icon: 'plug', color: '#3b82f6', title: 'Data flows in', desc: 'Connect Salesforce, email, Slack, files — or push your own. No schema to define.' },
  { icon: 'cognitive', color: '#8b5cf6', title: 'Auto-ontology', desc: 'Wexa discovers entities, relationships and facts automatically as data arrives.' },
  { icon: 'knowledgegraph', color: '#06b6d4', title: 'Self-organizing', desc: 'The context graph re-indexes and reshapes itself continuously — always current.' },
  { icon: 'rocket', color: '#8b5cf6', title: 'Served to any agent', desc: 'Query that context from any agent over MCP, API or SDK.' },
];

export type McpClientId = 'claude_ai' | 'chatgpt' | 'claude_desktop' | 'cursor' | 'other';

export interface McpClient {
  id: McpClientId;
  name: string;
  icon: string;
}

export const MCP_CLIENTS: McpClient[] = [
  { id: 'claude_ai', name: 'Claude.ai', icon: 'message' },
  { id: 'chatgpt', name: 'ChatGPT', icon: 'sparkles' },
  { id: 'claude_desktop', name: 'Claude Desktop', icon: 'terminal' },
  { id: 'cursor', name: 'Cursor', icon: 'edit' },
  { id: 'other', name: 'Other agent', icon: 'plug' },
];

export interface McpClientContent {
  type: 'hosted' | 'json';
  title?: string;
  intro: string;
  steps: string[];
}

export const MCP_CONTENT: Record<McpClientId, McpClientContent> = {
  claude_ai: {
    type: 'hosted',
    intro: 'Add Wexa as a custom connector in Claude.ai — no install required.',
    steps: [
      'Open Claude.ai → Settings → Connectors.',
      'Click “Add custom connector” and paste the Server URL on the right.',
      'Add the Authorization header with your API key and save. Claude now reasons over your live context.',
    ],
  },
  chatgpt: {
    type: 'hosted',
    intro: 'Connect Wexa to ChatGPT via a remote MCP server (Developer mode).',
    steps: [
      'In ChatGPT, open Settings → Connectors and turn on Developer mode.',
      'Choose “Add MCP server” and enter the Server URL on the right.',
      'Paste the Authorization Bearer token, then connect.',
    ],
  },
  claude_desktop: {
    type: 'json',
    title: 'claude_desktop_config.json',
    intro: 'Register Wexa in your Claude Desktop config file.',
    steps: [
      'Open Settings → Developer → Edit config (claude_desktop_config.json).',
      'Paste the block on the right and drop in your API key.',
      'Restart Claude Desktop — Wexa appears as an available connector.',
    ],
  },
  cursor: {
    type: 'json',
    title: '~/.cursor/mcp.json',
    intro: 'Add Wexa to Cursor’s MCP servers.',
    steps: [
      'Open Cursor → Settings → MCP → Add new server.',
      'Paste the configuration on the right.',
      'Reload — Wexa context tools are now available to the agent.',
    ],
  },
  other: {
    type: 'hosted',
    intro: 'Any MCP-compatible client can connect to Wexa over remote SSE.',
    steps: [
      'Point your client at the Server URL on the right.',
      'Pass the Authorization header with every request — org, project and grants come from the token.',
      'If your client uses a config file instead, use the JSON form shown.',
    ],
  },
};

export const MCP_SERVER_URL = 'https://mcp.wexa.ai/v1/sse';
export const API_BASE = 'https://api.wexa.ai/v1';

export interface SdkInfo {
  package: string;
  install: string;
}

/**
 * Connection coordinates that drive the connect surface. Sourced at runtime
 * from the api-gateway (`GET /v1/connection-info`, configured via its env) and
 * falls back to {@link DEFAULT_CONNECTION} until that resolves.
 */
export interface ConnectionInfo {
  mcpServerUrl: string; // MCP Streamable HTTP endpoint
  mcpSseUrl: string; // MCP SSE-compat endpoint
  apiBaseUrl: string; // REST base
  workspaceHeader: string;
  issuer: string;
  sdk: { python: SdkInfo; node?: SdkInfo };
  tools: string[];
}

export const DEFAULT_SDK: { python: SdkInfo; node?: SdkInfo } = {
  python: { package: 'wexa', install: 'pip install wexa' },
};

/** Fallback used before the gateway responds (and in isolated tests). Keeps the
 *  original hardcoded values so offline behavior is unchanged. */
export const DEFAULT_CONNECTION: ConnectionInfo = {
  mcpServerUrl: MCP_SERVER_URL,
  mcpSseUrl: MCP_SERVER_URL,
  apiBaseUrl: API_BASE,
  workspaceHeader: 'X-Wexa-Workspace',
  issuer: API_BASE.replace(/\/v1$/, ''),
  sdk: DEFAULT_SDK,
  tools: [],
};

/** Short, display-friendly form of a secret used inside snippets. */
export function shortToken(token: string): string {
  if (token.length <= 12) return token;
  return `${token.slice(0, 12)}…${token.slice(-4)}`;
}
